// Report versions for the redesigned Transfer Pricing Reports section. Sourced from the
// ICTPReportingOrchestrator GetTPReportVersions op (restJson1). Each version is one report-generation
// run (job) for a period; the UI uses them to populate the "Report Version" selector, drive the
// job-status bar, and gate the report cards.

/**
 * The kind of refresh a version represents, which decides card gating: a FULL run regenerates all
 * report types (MAR/CWB/LEF/VAR), a VAR_PLI run refreshes only the Variance Report and the PLI section.
 * The current orchestrator only produces full runs (every EDR trigger is a full refresh); VAR_PLI is
 * reserved for when the lighter refresh path ships.
 */
export enum ReportRunType {
    FULL = 'FULL',
    VAR_PLI = 'VAR_PLI',
}

/**
 * The orchestrator trigger channels (mirrors the backend TriggerEventType enum). Kept as a typed enum so
 * consumers (deriveRunType, deriveAccountingDay) reference named values instead of bare string literals.
 * The wire field {@link ReportVersionEntry.triggerType} is a raw string; compare against these values.
 */
export enum TriggerType {
    HANDOFF = 'HANDOFF',
    SOFT_CLOSE = 'SOFT_CLOSE',
    ON_DEMAND = 'ON_DEMAND',
}

/**
 * Raw entry shape returned by GetTPReportVersions (mirrors the orchestrator ReportVersionEntry Smithy
 * struct). Fields are the DTO wire contract; the UI-facing derived fields live on {@link ReportVersion}.
 */
export interface ReportVersionEntry {
    jobId: string;
    versionLabel: string;
    // JobStatus: PHASE1_RUNNING | PHASE2_RUNNING | COMPLETE | PARTIAL | PHASE1_TRIGGER_FAILED | PHASE2_TRIGGER_FAILED
    status: string;
    // Raw trigger channel; one of the TriggerType values (HANDOFF | SOFT_CLOSE | ON_DEMAND).
    triggerType: string;
    phase1StartTime: string;
    completedAt?: string;
    partialFlag?: boolean;
    watchdogAdvancedPhase?: string;
    startedBy?: string;
}

/** Raw GetTPReportVersions response (ordered by phase1StartTime DESC). */
export interface GetTPReportVersionsResponse {
    versions: ReportVersionEntry[];
}

/** A report version enriched with the UI-facing derived fields used by the redesigned section. */
export interface ReportVersion extends ReportVersionEntry {
    /** True while the run is still generating (PHASE1_RUNNING / PHASE2_RUNNING). */
    inProgress: boolean;
    /** The refresh kind that drives card enable/disable (see {@link ReportRunType}). */
    runType: ReportRunType;
}

/** What the getReportVersions hook returns: the enriched versions plus the period's QEC flag. */
export interface ReportVersionsResult {
    versions: ReportVersion[];
    isQuarterEndPeriod: boolean;
}

// Status buckets that drive how a version appears in the Report Version selector:
//  - in progress -> shown but NOT selectable (the report isn't ready yet)
//  - failed      -> hidden entirely (nothing to view)
//  - COMPLETE / PARTIAL (neither) -> shown and selectable
// PENDING_FDV is a run the orchestrator has accepted but not yet promoted to a JobRegistry row (it is
// awaiting FDV data freshness); GetTPReportVersions surfaces it as a synthetic in-progress entry so a
// triggered-but-not-yet-running refresh still gates the UI (disabled dropdown option + Full Refresh).
const IN_PROGRESS_STATUSES = new Set(['PENDING_FDV', 'PHASE1_RUNNING', 'PHASE2_RUNNING']);
const FAILED_STATUSES = new Set(['PHASE1_TRIGGER_FAILED', 'PHASE2_TRIGGER_FAILED']);

/** Derives {@link ReportVersion.inProgress} from the raw JobStatus. */
export function isVersionInProgress(status: string): boolean {
    return IN_PROGRESS_STATUSES.has(status);
}

/** True for a trigger-failed run (PHASE1/2_TRIGGER_FAILED) — such runs are hidden from the selector. */
export function isVersionFailed(status: string): boolean {
    return FAILED_STATUSES.has(status);
}

/**
 * Derives the {@link ReportRunType} from the raw triggerType. Every current orchestrator run
 * (HANDOFF / SOFT_CLOSE / ON_DEMAND) is a full refresh; VAR_PLI is only returned once the backend
 * distinguishes the lighter refresh path.
 */
export function deriveRunType(triggerType: string): ReportRunType {
    return triggerType === ReportRunType.VAR_PLI ? ReportRunType.VAR_PLI : ReportRunType.FULL;
}

/** Enriches a raw entry with the UI-facing derived fields. */
export function toReportVersion(entry: ReportVersionEntry): ReportVersion {
    return {
        ...entry,
        inProgress: isVersionInProgress(entry.status),
        runType: deriveRunType(entry.triggerType),
    };
}
